import { mapGetters } from "vuex";
import searchProductsByIdsQuery from "@/graphql/queries/SearchProductsByIds.gql";
import { PER_PAGE } from "@/helpers/pagination";
import { getFeatureToggles } from "@/helpers/featureToggles";
import { nostoCollectionSortFields } from "@/config";
import {
  nostoCategoryProducts,
  recordNostoSearchClick,
  toNostoProductId,
} from "@/helpers/nostoSearch";

/**
 * Nosto Category Merchandising 2.0 for collection pages.
 *
 * With CM 1.0 Nosto wrote its sort order into each Shopify collection's manual
 * sort, so the theme simply read the collection with `sortKey: MANUAL`. With
 * CM 2.0 the storefront asks Nosto's Search & Categories API for the ordered
 * product ids at page load (the same API the search page uses), and hydrates
 * the cards from Shopify so the grid is unchanged.
 *
 * The host view keeps its Shopify `collection` query for everything that is
 * not the product listing (header image, description, product-type tabs,
 * featured cards) and hands the result to `nostoReceiveShopifyCollection`.
 * This mixin merges Nosto's products into it.
 *
 * Behaviour:
 * - Off unless the `nostoCategoryEnabled` toggle is on AND Liquid passed the
 *   collection id/title (`collection-id`, `collection-title`).
 * - If the first unfiltered Nosto request fails or returns nothing (e.g. a
 *   brand-new collection not yet synced to Nosto), the page falls back to the
 *   Shopify listing for this visit (`nostoActive` -> false).
 * - Later failures (sort/filter/load more) keep what is on screen.
 *
 * Hooks a host view can override:
 * - nostoApplyCollection(collection): default commits to the collection store.
 * - nostoSetLoading(value):           default calls the store's setLoading.
 */
export default {
  props: {
    collectionId: {
      type: [String, Number],
      default: null,
    },
    collectionTitle: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      nostoActive: false,
      nostoSuspended: false,
      nostoStarted: false,
      nostoLoaded: false,
      nostoFrom: 0,
      nostoTotal: 0,
      nostoEdges: [],
      nostoFacets: [],
      nostoSelectedFilters: [],
      nostoRequestId: 0,
      nostoPageSize: PER_PAGE,
      shopifyCollection: null,
    };
  },
  computed: {
    ...mapGetters("app", ["currencyCode"]),
    useNostoCategory() {
      const { nostoCategoryEnabled = false } = getFeatureToggles();
      return (
        nostoCategoryEnabled &&
        Boolean(this.collectionId || this.collectionTitle)
      );
    },
  },
  watch: {
    currencyCode() {
      this.maybeStartNostoCollection();
    },
  },
  created() {
    this.nostoActive = this.useNostoCategory;
  },
  mounted() {
    this.maybeStartNostoCollection();
  },
  methods: {
    // ---- hooks -----------------------------------------------------------
    nostoApplyCollection(collection) {
      this.setCollection(collection);
    },
    nostoSetLoading(value) {
      if (typeof this.setLoading === "function") {
        this.setLoading(value);
      }
    },

    // ---- lifecycle -------------------------------------------------------
    maybeStartNostoCollection() {
      if (!this.useNostoCategory || this.nostoStarted) {
        return;
      }

      // The Nosto account id is derived from the currency, which is resolved
      // asynchronously from the shop query. Wait for it.
      if (!this.currencyCode) {
        return;
      }

      this.nostoStarted = true;
      this.fetchNostoCollection({ reset: true });
    },

    /**
     * Called by the host view's Shopify `collection` query result. Returns
     * true when Nosto owns the listing (so the host must not commit the
     * Shopify products itself).
     */
    nostoReceiveShopifyCollection(collection) {
      this.shopifyCollection = collection;

      if (!this.nostoActive) {
        return false;
      }

      this.syncNostoCollection();

      if (this.nostoLoaded) {
        this.nostoSetLoading(false);
      }

      return true;
    },

    // ---- requests --------------------------------------------------------
    nostoSortParam() {
      const { value, reverse } = this.sort || {};
      const entry = nostoCollectionSortFields[value];

      if (!entry) {
        return [];
      }

      const order =
        value === "PRICE" ? (reverse ? "desc" : "asc") : entry.order;

      return [{ field: entry.field, order }];
    },

    nostoTypeFilters() {
      // Product-type tabs store Shopify-style inputs ({ productType: "X" })
      // so the tab highlighting keeps working; translate them for Nosto.
      const types = (this.filters || [])
        .map((filter) => filter.productType)
        .filter(Boolean);

      return types.length ? [{ field: "productType", value: types }] : [];
    },

    async hydrateNostoProductIds(productIds) {
      if (!productIds.length) {
        return [];
      }

      const ids = productIds.map((id) => `gid://shopify/Product/${id}`);

      const { data } = await this.$apollo.query({
        query: searchProductsByIdsQuery,
        variables: { ids },
      });

      // nodes(ids:) preserves the requested (Nosto-ranked) order; null entries
      // are dropped (unpublished or not available in this region).
      return (data?.nodes || []).filter(Boolean);
    },

    async fetchNostoCollection({ reset = false, size = null } = {}) {
      const requestId = ++this.nostoRequestId;
      // Jewellery title groups (earrings, necklaces, ...) are filtered client
      // side from one large page and hide pagination, so every refetch for them
      // (sort, facet, tab) must keep the large page, not just the first load.
      const pageSize = size || (this.titleGroup ? 250 : this.nostoPageSize);
      const from = reset ? 0 : this.nostoFrom;
      const isInitialLoad = !this.nostoLoaded;

      this.nostoSetLoading(true);

      try {
        const { productIds, total, facets, ok } = await nostoCategoryProducts({
          currencyCode: this.currencyCode,
          categoryId: this.collectionId,
          categoryPath: this.collectionTitle,
          size: pageSize,
          from,
          filters: [...this.nostoTypeFilters(), ...this.nostoSelectedFilters],
          sort: this.nostoSortParam(),
          track: "category",
        });

        if (requestId !== this.nostoRequestId) {
          return; // superseded by a newer request
        }

        const hasRefinements =
          this.nostoSelectedFilters.length > 0 ||
          this.nostoTypeFilters().length > 0 ||
          this.nostoSortParam().length > 0;

        if (!ok || (isInitialLoad && !hasRefinements && total === 0)) {
          if (isInitialLoad) {
            this.nostoFallbackToShopify();
          }
          return;
        }

        const products = await this.hydrateNostoProductIds(productIds);

        if (requestId !== this.nostoRequestId) {
          return;
        }

        const newEdges = products.map((node, index) => ({
          cursor: String(from + index),
          node,
        }));

        this.nostoEdges = reset ? newEdges : [...this.nostoEdges, ...newEdges];
        this.nostoTotal = total;
        this.nostoFacets = facets || [];
        this.nostoFrom = from + pageSize;
        this.nostoLoaded = true;

        this.syncNostoCollection();
      } catch (error) {
        console.error("Nosto collection error:", error);
        if (isInitialLoad) {
          this.nostoFallbackToShopify();
        }
      } finally {
        // Keep the skeleton up until the Shopify metadata has also arrived;
        // nostoReceiveShopifyCollection clears it then.
        if (requestId === this.nostoRequestId && this.shopifyCollection) {
          this.nostoSetLoading(false);
        }
      }
    },

    syncNostoCollection() {
      const base = this.shopifyCollection;

      if (!base) {
        return;
      }

      const hasNextPage = this.nostoFrom < this.nostoTotal;

      this.nostoApplyCollection({
        ...base,
        totalCount: this.nostoTotal,
        products: {
          ...base.products,
          pageInfo: { hasNextPage },
          edges: this.nostoEdges,
        },
      });
    },

    nostoFallbackToShopify() {
      this.nostoActive = false;
      this.nostoLoaded = true;
      this.nostoEdges = [];
      this.nostoFacets = [];
      this.nostoSelectedFilters = [];

      if (this.shopifyCollection) {
        this.nostoApplyCollection(this.shopifyCollection);
        this.nostoSetLoading(false);
      }
    },

    // ---- interactions ----------------------------------------------------
    nostoRefetch({ size = null } = {}) {
      return this.fetchNostoCollection({ reset: true, size });
    },

    nostoLoadMore() {
      if (this.loading || this.nostoFrom >= this.nostoTotal) {
        return;
      }

      return this.fetchNostoCollection({ reset: false });
    },

    nostoFilterChange(filters) {
      this.nostoSelectedFilters = filters;
      return this.nostoRefetch();
    },

    /**
     * Product-type tab change. Returns true when Nosto handled it; false when
     * the host should take the Shopify path.
     *
     * A metafield tab (e.g. "12 Days of Discounts") is one Nosto cannot
     * express, so the listing is handed to Shopify until the shopper picks a
     * tab Nosto can serve again ("Shop all" or a product type).
     */
    nostoTypeChange(filters) {
      if (!this.nostoActive && !this.nostoSuspended) {
        return false;
      }

      const key = Object.keys(filters || {})[0];
      const value = filters?.[key];

      if (key === "productMetafield" && value) {
        this.nostoSuspended = true;
        this.nostoActive = false;
        return false;
      }

      if (this.nostoSuspended) {
        this.nostoSuspended = false;
        this.nostoActive = true;
      }

      const keysToFilter = ["productType", "productMetafield"];
      const filtersWithoutTypes = (this.filters || []).filter(
        (filter) => !keysToFilter.includes(Object.keys(filter)[0])
      );

      if (key === "productType" && value) {
        filtersWithoutTypes.push({ productType: value });
      }

      this.setFilters(filtersWithoutTypes);
      this.nostoRefetch();
      return true;
    },

    nostoProductClick(product) {
      if (!this.nostoActive) {
        return;
      }

      recordNostoSearchClick("category", {
        productId: toNostoProductId(product?.id),
        url: product?.onlineStoreUrl,
      });
    },
  },
};
